"use client";

import ENDPOINTS from "@/lib/endpoints";
import {
  basePath,
  clearAuthCookies,
  getCustomCaptcha,
  getFingerPrint,
  getGoogleCaptcha,
  notifyToast,
  request,
  setAccessTokenCookie,
  setLoggedInCookie,
  setRefreshTokenCookie,
} from "@/lib/helpers";
import * as Yup from "yup";
import { deleteCookie, setCookie } from "cookies-next";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import { useDispatch } from "react-redux";
import { fetchGoogleCaptcha } from "@/store/captchaSlice";
import { setUserData } from "@/store/userSlice";
import useDeviceName from "./useDeviceName";

export default function useLoginHandler() {
  const router = useRouter();
  const dispatch = useDispatch();

  const initialValues = {
    username: "",
    password: "",
    "g-recaptcha-response": "",
    captcha_secret: "",
  };

  const validationSchema = () => {
    const customCaptcha = getCustomCaptcha();
    const googleCaptcha = getGoogleCaptcha();

    return Yup.object().shape({
      username: Yup.string().required("Username is required"),
      password: Yup.string().required("Password is required"),
      captcha: customCaptcha?.data?.extension
        ? Yup.string().required("Please complete the reCAPTCHA")
        : Yup.string().nullable(),
      "g-recaptcha-response": googleCaptcha?.data?.extension
        ? Yup.string().required("Google recaptcha is required")
        : Yup.string().nullable(),
    });
  };

  const handleSubmit = async (values, { resetForm }) => {
    const deviceName = useDeviceName();
    values.is_web = 1;
    values.fingerprint = await getFingerPrint();
    values.device_name = deviceName;

    try {
      const { data } = await request.post(ENDPOINTS.LOGIN, values);

      if (data.status == "error") {
        let shown = false;
        if (data.message) {
          let msg = data.message;
          if (typeof msg === "object") {
            if (msg.error && Array.isArray(msg.error)) {
              msg = msg.error.join(" ");
            } else {
              msg = JSON.stringify(msg);
            }
          }
          if (msg) {
            toast.error(msg);
            shown = true;
          }
        }
        if (!shown) {
          notifyToast(data);
        }
        dispatch(fetchGoogleCaptcha(data.data));
        return false;
      }
      let accessToken = data.data.access_token;
      let userData = data.data.user;

      if (Number(userData?.ev) === 0) {
        clearAuthCookies();
        dispatch(setUserData(null));
        if (userData?.email) {
          sessionStorage.setItem("pending_email_verification", userData.email);
        }
        router.push("/verify-email");
        return;
      }

      // If OTP is not required, mark as verified for middleware
      if (!data.data.require_otp) {
        userData.tv = 1;
      }

      const refreshToken = data.data.refresh_token;
      setAccessTokenCookie(accessToken);
      setRefreshTokenCookie(refreshToken);
      setLoggedInCookie(true);
      setCookie("user_data", JSON.stringify(userData));
      dispatch(setUserData(userData));

      if (data.data.require_otp) {
        router.push("/authorization");
      } else {
        const isBusinessUser = Number(userData.type ?? userData.user_type) === 2;
        if (isBusinessUser) {
          const businessPortalUrl = process.env.NEXT_PUBLIC_BUSINESS_PORTAL_URL || "https://business.belyfted.com";
          const { data: encryptData } = await request.get(`${ENDPOINTS.ENCRYPT_PAYLOAD}/${userData.id}`);
          const ssoToken = encryptData?.data?.encrypted ?? userData.id;
          localStorage.setItem("sso_user_id", String(userData.id));
          window.location.href = `${businessPortalUrl}/login/${ssoToken}`;
        } else {
          router.push("/user/dashboard");
        }
      }
    } catch (error) {
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        
        // Check for rate limiting (429)
        if (error.response.status === 429) {
          let msg =
            errorData.message ||
            "Too many login attempts. Please try again later.";
          toast.error(msg);
        } 
        // Handle error messages from the API
        else if (errorData.message && errorData.message.error) {
          errorData.message.error.forEach((message) => {
            toast.error(message);
          });
        } else {
          toast.error("An unexpected error occurred. Please try again.");
        }
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
      return false;
    }
  };

  const logout = async () => {
    await request.get(ENDPOINTS.LOGOUT);
    deleteCookie("access-token");
    deleteCookie("refresh-token");
    deleteCookie("is_logged_in");
    deleteCookie("user_data");
    dispatch(setUserData(null));

    window.location.reload(true);
    toast.success("Logged out successfully");
  };

  return { initialValues, validationSchema, handleSubmit, logout };
}